// Copyright 2021 Amazon.com, Inc. or its affiliates. All Rights Reserved.
import React, { useEffect, useState } from "react";
import { AuthStatus, ErrorType, LandingPageState } from "../../enums";
import BrowserFactory from "../browser/BrowserFactory";
import EmbeddedStreamer from "../browser/appStream/EmbeddedStreamer";
import log, { METRIC_NAME } from "../../logging";
import WarningAlert from "../general/WarningAlert";
import {
    isDeviceOnExperimentalMode,
    isWebcamSupportedWithDocker,
} from "../../utils/userAgentUtils";
import { useTranslation } from "react-i18next";
import ErrorAlert from "../general/ErrorAlert";
import { ERROR_STATE } from "../../constants";
import { useSetCookiesFromUrlParams } from "../../hooks/useSetCookiesFromUrlParams";
import { isBrowserPermissionGranted } from "../../utils/toolbarItemUtils";
import { WebcamModal } from "../presession/WebcamModal";
import { useSetDeepLinkFromUrlParam } from "../../hooks/useSetDeepLinkFromUrlParam";
import { getRebrandedTranslation } from "../../i18n";
import { useAppInitialization } from "../../hooks/useAppInitialization";
import { useCustomBrandingAssetStore } from "../../hooks/useCustomBrandingAsset";
import { TermsOfServiceModal } from "../presession/TermsOfServiceModal";
import EUCSignInPage from "../authentication/signIn/EUCSignInPage";
import { useSignOut } from "../../hooks/api/useSignOut";
import { useAsyncEffect } from "use-async-effect";
import { useAuthCheck } from "../../hooks/api/useAuthCheck";

export const App = () => {
    const settingCookiesFromUrlParamsStatus = useSetCookiesFromUrlParams();
    const settingDeepLinkStatus = useSetDeepLinkFromUrlParam();

    useAppInitialization({ settingCookiesFromUrlParamsStatus });

    const signOut = useSignOut();

    const {
        authCheckResponseId,
        userInfo,
        isCheckingAuth,
        isAuthCheckError,
    } = useAuthCheck();

    const [authStatus, setAuthStatus] = useState(AuthStatus.LOADING);
    useAsyncEffect(async () => {
        // TODO [LOWA-30289] Move user authentication logic to a single component (preferably sign-in component)
        if (isCheckingAuth) {
            return;
        }
        if (isAuthCheckError) {
            setAuthStatus(AuthStatus.SIGNED_OUT);
            return;
        }

        // Success
        window.userName = userInfo.username;
        window.userSub = userInfo.userId;
        setAuthStatus(AuthStatus.SIGNED_IN);
    }, [authCheckResponseId, isCheckingAuth, isAuthCheckError]);

    useAsyncEffect(async () => {
        // TODO [LOWA-30289] use react-router or re-structure the components to avoid re-rendering
        if (authStatus === AuthStatus.SIGNING_OUT) {
            try {
                const data = await signOut();
                window.location.href = data.Location;
            } catch {
                // Do nothing.
            }
        }
    }, [authStatus]);

    const [browserEndpoint, setBrowserEndpoint] = useState<string | null>(null);
    const [errorState, setErrorState] = useState(() => {
        const errorCode = new URLSearchParams(window.location.search).get(
            "error"
        );
        const errorDescription = new URLSearchParams(
            window.location.search
        ).get("error_description");
        switch (errorCode) {
            case ErrorType.UNAUTHORIZED: {
                if (errorDescription) {
                    return {
                        errorState: ERROR_STATE.ERROR,
                        errorHeader: "authentication.errorAlert.error.header",
                        errorMessage: errorDescription,
                        landingPageState: LandingPageState.FULL_PAGE,
                    };
                } else {
                    return {
                        errorState: ERROR_STATE.INFO,
                        errorHeader: "login.errorAlert.unauthorized.header",
                        errorMessage: "login.errorAlert.unauthorized.message",
                        landingPageState: LandingPageState.BANNER_ONLY,
                    };
                }
            }
            default: {
                return {
                    errorState: ERROR_STATE.NONE,
                    errorHeader: null,
                    errorMessage: null,
                };
            }
        }
    });
    const [redirectToLogin, setRedirectToLogin] = useState(false);
    const [showWebcamModal, setShowWebcamModal] = useState(false);

    useAsyncEffect(async () => {
        if (!isWebcamSupportedWithDocker()) {
            // Early exit before async checking
            return;
        }
        if (await isBrowserPermissionGranted("camera")) {
            // Webcam permission granted
            return;
        }
        setShowWebcamModal(true);
    }, []);

    const { t } = useTranslation();
    const isCustomBrandingResourceReady = useCustomBrandingAssetStore(
        (store) => store.isResourceReady
    );
    const hasTermsOfService = useCustomBrandingAssetStore(
        (store) => store.hasTermsOfService
    );
    const [termsOfServiceAccepted, setTermsOfServiceAccepted] = useState(false);

    useEffect(() => {
        log.publishCounterMetric(METRIC_NAME.REFRESH);

        /*
      Track page refresh events using an experimental api, this is not supported in
      iE or safari. The alternative to listen to page refresh page events will never work
      because the publish fn is async and batched so it will not finish before the browser
      kills the thread.
    */
        if (performance.getEntriesByType) {
            const perfEntries = performance.getEntriesByType("navigation");
            // eslint-disable-next-line
      // @ts-ignore spec is here https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming/type
            if (perfEntries[0] && perfEntries[0].type === "reload") {
                log.publishCounterMetric(METRIC_NAME.CLICK);
            }
        }
    }, []);

    // Wait until cookies from URL params are loaded
    if (settingCookiesFromUrlParamsStatus !== "completed") {
        return <></>;
    }
    // Wait until deeplink is stored in sessionStorage
    if (settingDeepLinkStatus !== "completed") {
        return <></>;
    }
    // Wait until custom branding resource is loaded
    if (!isCustomBrandingResourceReady) {
        return <></>;
    }
    // Wait until checking auth status
    if (authStatus === AuthStatus.LOADING) {
        return <></>;
    }

    if (authStatus === AuthStatus.SIGNED_IN && !redirectToLogin) {
        // Terms of service modal
        if (hasTermsOfService && !termsOfServiceAccepted) {
            return (
                <TermsOfServiceModal
                    onCancel={() => {
                        setTermsOfServiceAccepted(false);
                        setRedirectToLogin(true);
                    }}
                    onProceed={() => {
                        setTermsOfServiceAccepted(true);
                    }}
                />
            );
        }

        // Valid user, render the main page
        if (showWebcamModal) {
            return (
                <WebcamModal
                    dismissWebcamModal={() => setShowWebcamModal(false)}
                />
            );
        }

        if (!browserEndpoint) {
            return (
                <BrowserFactory
                    onBrowserCreation={(browserEndpoint) => {
                        setBrowserEndpoint(browserEndpoint);
                    }}
                    onRedirectToLogin={setRedirectToLogin}
                />
            );
        }

        return (
            <>
                <WarningAlert
                    title={getRebrandedTranslation(
                        "experimentalMode.alert.title",
                        t
                    )}
                    message={getRebrandedTranslation(
                        "experimentalMode.alert.message",
                        t
                    )}
                    render={isDeviceOnExperimentalMode()}
                />
                <EmbeddedStreamer
                    endpoint={browserEndpoint}
                    onSignOut={setAuthStatus}
                    onRedirectToLogin={setRedirectToLogin}
                />
            </>
        );
    }

    if (authStatus === AuthStatus.SIGNING_OUT) {
        // Redirect to SignOutResponse location after sign-out
        return null;
    }

    if (errorState.errorState !== ERROR_STATE.NONE) {
        if (LandingPageState.BANNER_ONLY === errorState.landingPageState) {
            return (
                <div className="awsui On-top">
                    <ErrorAlert
                        type={errorState.errorState}
                        header={getRebrandedTranslation(
                            errorState.errorHeader,
                            t
                        )}
                        message={getRebrandedTranslation(
                            errorState.errorMessage,
                            t
                        )}
                        onDismissErrorAlert={() => {
                            setErrorState({
                                errorState: ERROR_STATE.NONE,
                                errorHeader: null,
                                errorMessage: null,
                            });
                        }}
                    />
                </div>
            );
        }

        return <EUCSignInPage errorState={errorState} />;
    }

    return <EUCSignInPage />;
};

export default App;
